import axios from 'axios';
import config from '../../config';

const SKEW_MS = 60 * 1000; // refresh 60s before expiry

class TokenApi {
  /** Read auth bundle from localStorage */
  static _read() {
    try {
      const raw = localStorage.u;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.token || !parsed.user) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  /** Persist auth bundle back to localStorage */
  static _write(bundle) {
    try {
      localStorage.u = JSON.stringify(bundle);
    } catch (_) {}
  }

  /** Determine expiration from either ISO expiresIn or jwt exp */
  static _isExpired(token) {
    if (!token) return true;
    // Prefer ISO string in expiresIn (your app stores an ISO string)
    const iso = token.expiresIn;
    if (iso) {
      const expMs = Date.parse(iso);
      if (!Number.isNaN(expMs)) return Date.now() + SKEW_MS >= expMs;
    }
    // Fallback: decode JWT `exp` (seconds since epoch)
    try {
      const [, payloadB64] = String(token.accessToken || '').split('.');
      if (payloadB64) {
        const json = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
        if (json && json.exp) {
          const expMs = json.exp * 1000;
          return Date.now() + SKEW_MS >= expMs;
        }
      }
    } catch (_) {}
    // If we can't tell, assume expired
    return true;
  }

  /** Legacy synchronous accessor (does not refresh). */
  static GetToken() {
    const store = this._read();
    if (!store) return '';
    const t = store.token;
    if (!t || !t.accessToken) return '';
    return `${t.tokenType || 'Bearer'} ${t.accessToken}`;
  }

  /** Preferred accessor: ensures a fresh token, returns `"Bearer xxx"` string */
  static async GetTokenAsync() {
    const token = await this.ensureToken();
    return token ? `${token.tokenType || 'Bearer'} ${token.accessToken}` : '';
  }

  /** Ensure token is fresh; if expired, call refresh endpoint and persist result */
  static async ensureToken() {
    const store = this._read();
    if (!store) return null;
    const { token, user } = store;

    // If not expired, return as-is
    if (!this._isExpired(token)) return token;

    // Expired – try refresh
    if (!user?.email || !token?.refreshToken) return null;
    try {
      const url = `${config.api}auth/refresh-token`;
      const payload = { email: user.email, refreshToken: token.refreshToken };
      const { data } = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      // Expected response shape:
      // { tokenType, accessToken, refreshToken, expiresIn }
      const next = { ...store, token: data };
      this._write(next);
      return next.token;
    } catch (err) {
      // Optional: clear invalid token on 401
      // console.error('refresh-token failed', err);
      return null;
    }
  }
}

export default TokenApi;
